import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener(ThemeEvents.quantitySelectorUpdate, this.updatePrice);
    closestSection.addEventListener('change', this.#onSubscriptionChange);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.updatePrice);
    closestSection.removeEventListener('change', this.#onSubscriptionChange);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.type === ThemeEvents.quantitySelectorUpdate) {
      this.#updatePriceForQuantity(event.detail?.quantity);
      return;
    }

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = this.#getMatchingPriceContainerFromHtml(event.detail.data.html);
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    const root = this.closest('.shopify-section, dialog');
    const qty = this.#getQuantity(root, undefined);
    this.#applyQuantityPricing(newPrice, root, qty, event.detail.data.html);

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }

    // Re-apply quantity/subscription pricing on the live DOM after morph.
    requestAnimationFrame(() => {
      this.#updatePriceForQuantity();
    });
  };

  /**
   * Updates the current price display for quantity changes.
   * @param {number} [quantityOverride]
   */
  #updatePriceForQuantity(quantityOverride) {
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (!currentPrice) return;

    const root = this.closest('.shopify-section, dialog');
    this.#applyQuantityPricing(currentPrice, root, quantityOverride);
  }

  /**
   * Applies quantity + subscription pricing to the provided price container.
   * @param {Element} priceContainer
   * @param {ParentNode | null} root
   * @param {number} [quantityOverride]
   * @param {ParentNode | null} [selectionRoot]
   */
  #applyQuantityPricing(priceContainer, root, quantityOverride, selectionRoot = null) {
    if (!root) return;

    const quantity = this.#getQuantity(root, quantityOverride);
    const selectionRootToUse = selectionRoot || root;
    const selection = this.#getActiveSubscriptionSelection(selectionRootToUse);
    if (!Number.isFinite(quantity) || quantity <= 0) return;

    if (!selection) {
      let baseCents = parseInt(priceContainer.dataset.basePriceCents || '0', 10);
      if (!Number.isFinite(baseCents) || baseCents <= 0) {
        const displayedCents = this.#getDisplayedPriceCents(priceContainer);
        if (!Number.isFinite(displayedCents) || displayedCents <= 0) return;
        const lastQuantity = parseInt(priceContainer.dataset.lastQuantity || '0', 10);
        const divisor = Number.isFinite(lastQuantity) && lastQuantity > 0 ? lastQuantity : 1;
        baseCents = Math.round(displayedCents / divisor);
        if (Number.isFinite(baseCents) && baseCents > 0) {
          priceContainer.dataset.basePriceCents = baseCents.toString();
        }
      }
      if (!Number.isFinite(baseCents) || baseCents <= 0) return;
      const totalFallback = baseCents * quantity;
      const priceEl = priceContainer.querySelector('.price');
      if (priceEl) {
        const template = getMoneyFormat(priceContainer, root, '');
        priceEl.textContent = formatMoneyWithTemplate(
          totalFallback,
          template,
          extractCurrencyCode(priceEl.textContent || '')
        );
      }
      priceContainer.dataset.lastQuantity = quantity.toString();
      return;
    }

    priceContainer.dataset.basePriceCents = selection.priceCents.toString();
    const totalPrice = selection.priceCents * quantity;
    const totalCompareAt = selection.compareAtCents > 0 ? selection.compareAtCents * quantity : 0;

    const priceEl = priceContainer.querySelector('.price');
    if (priceEl) {
      const template = getMoneyFormat(priceContainer, root, selection.currency);
      priceEl.textContent = formatMoneyWithTemplate(totalPrice, template, selection.currency);
    }

    const compareEl = priceContainer.querySelector('.compare-at-price');
    if (compareEl && totalCompareAt > totalPrice) {
      const template = getMoneyFormat(priceContainer, root, selection.currency);
      compareEl.textContent = formatMoneyWithTemplate(totalCompareAt, template, selection.currency);
    }
    priceContainer.dataset.lastQuantity = quantity.toString();
  }

  /**
   * Gets current quantity from the root or override.
   * @param {ParentNode} root
   * @param {number} [quantityOverride]
   * @returns {number}
   */
  #getQuantity(root, quantityOverride) {
    if (Number.isFinite(quantityOverride) && quantityOverride > 0) return quantityOverride;

    const ownForm = this.closest('product-form-component');
    const qtyEl =
      ownForm?.querySelector?.('[ref="quantityInput"]') || root.querySelector?.('product-form-component [ref="quantityInput"]');
    if (qtyEl instanceof HTMLInputElement) {
      const qty = parseInt(qtyEl.value, 10);
      if (Number.isFinite(qty) && qty > 0) return qty;
    }

    return 1;
  }

  /**
   * Gets active subscription/one-time selection from the visible block.
   * @param {ParentNode} root
   * @returns {{priceCents: number, compareAtCents: number} | null}
   */
  #getActiveSubscriptionSelection(root) {
    const ownForm = this.closest('product-form-component');
    const scopeRoot = ownForm || root;

    const activeBlock = scopeRoot.querySelector?.(
      '.shopify_subscriptions_app_block:not(.shopify_subscriptions_app_block--hidden)'
    );
    const scope = activeBlock || scopeRoot;
    const selectedOption = scope.querySelector?.('input[data-radio-type]:checked') ||
      scope.querySelector?.('input[data-radio-type="one_time_purchase"]');

    if (!(selectedOption instanceof HTMLInputElement)) return null;
    if (!selectedOption.dataset.variantPrice) return null;

    const priceText = selectedOption.dataset.variantPrice;
    const priceCents = parseVariantPriceToCents(priceText);
    if (!Number.isFinite(priceCents) || priceCents <= 0) return null;

    const compareAtCents = selectedOption.dataset.variantCompareAtPrice
      ? parseVariantPriceToCents(selectedOption.dataset.variantCompareAtPrice)
      : 0;
    const currency = extractCurrencyCode(priceText);

    return {
      priceCents,
      compareAtCents: Number.isFinite(compareAtCents) ? compareAtCents : 0,
      currency,
    };
  }

  /**
   * Handles subscription radio changes.
   * @param {Event} event
   */
  #onSubscriptionChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.matches('input[data-radio-type]')) return;
    if (!event.target.closest('.shopify_subscriptions_app_container')) return;
    requestAnimationFrame(() => {
      this.#updatePriceForQuantity();
    });
  };

  /**
   * Gets cents from current displayed price.
   * @param {Element} priceContainer
   * @returns {number}
   */
  #getDisplayedPriceCents(priceContainer) {
    const priceEl = priceContainer.querySelector('.price');
    if (!priceEl) return NaN;
    return parseVariantPriceToCents(priceEl.textContent || '');
  }

  /**
   * Gets the matching price container from fetched HTML using position in current section/dialog.
   * This avoids reading the wrong price when sticky and main forms both exist.
   * @param {Document} html
   * @returns {Element | null}
   */
  #getMatchingPriceContainerFromHtml(html) {
    const root = this.closest('.shopify-section, dialog');
    const productId = this.dataset.productId;
    const selector = productId ? `product-price[data-product-id="${productId}"]` : 'product-price';

    const fallback = html.querySelector('product-price [ref="priceContainer"]');
    if (!root) return html.querySelector(`${selector} [ref="priceContainer"]`) || fallback;

    const currentPrices = Array.from(root.querySelectorAll(selector));
    const currentIndex = currentPrices.indexOf(this);

    let responseRoot = html;
    if (root instanceof HTMLElement && root.id) {
      const escapedId = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(root.id) : root.id;
      const matchedRoot = html.querySelector(`#${escapedId}`);
      if (matchedRoot) responseRoot = matchedRoot;
    }

    const responsePrices = Array.from(responseRoot.querySelectorAll(selector));
    const matchedPrice = (currentIndex >= 0 ? responsePrices[currentIndex] : responsePrices[0]) || null;

    return matchedPrice?.querySelector('[ref="priceContainer"]') || fallback;
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}

/**
 * Parses a variant price dataset value into cents.
 * @param {string} value
 * @returns {number}
 */
function parseVariantPriceToCents(value) {
  const trimmed = value.trim();
  if (!trimmed) return NaN;

  if (/^[0-9]+$/.test(trimmed)) {
    return parseInt(trimmed, 10);
  }

  const normalized = normalizeNumberString(trimmed);
  if (!normalized) return NaN;

  const numeric = Number(normalized);
  if (!Number.isFinite(numeric)) return NaN;

  return Math.round(numeric * 100);
}

/**
 * Normalizes a formatted number string into a dot-decimal number.
 * @param {string} value
 * @returns {string}
 */
function normalizeNumberString(value) {
  let cleaned = value.replace(/\s/g, '').replace(/'/g, '');
  const commaIndex = cleaned.lastIndexOf(',');
  const dotIndex = cleaned.lastIndexOf('.');

  if (commaIndex !== -1 && dotIndex !== -1) {
    if (commaIndex > dotIndex) {
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      cleaned = cleaned.replace(/,/g, '');
    }
  } else if (commaIndex !== -1 && dotIndex === -1) {
    cleaned = cleaned.replace(',', '.');
  }

  return cleaned.replace(/[^\d.]/g, '');
}

/**
 * Gets the shop money format template.
 * @returns {string}
 */
function getMoneyFormat(priceContainer, root, currency) {
  const productPrice = priceContainer?.closest?.('product-price');
  const moneyTemplate = productPrice?.querySelector?.('template[ref="moneyFormat"]');
  const inlineFormat = moneyTemplate?.content?.textContent?.trim() || '';
  if (inlineFormat) return inlineFormat;

  const addToCartContainer = root?.querySelector?.('add-to-cart-component[data-money-format]');
  const format = addToCartContainer?.getAttribute('data-money-format') || '';
  const formatWithCurrency = addToCartContainer?.getAttribute('data-money-format-with-currency') || '';
  if (currency && formatWithCurrency && formatWithCurrency.includes('currency')) return formatWithCurrency;
  return format || window.shopMoneyFormat || '{{amount}}';
}

/**
 * Extracts currency code from a formatted price string.
 * @returns {string}
 */
function extractCurrencyCode(text) {
  const match = text.match(/\\b[A-Z]{3}\\b/);
  if (match && match[0]) return match[0];
  return (window.Shopify?.currency?.active || '').toUpperCase();
}

/**
 * Formats money using a Shopify money format template.
 * @param {number} moneyValue - The money value in cents.
 * @param {string} template - The money format template.
 * @param {string} currency - The currency ISO code.
 * @returns {string} The formatted money string.
 */
function formatMoneyWithTemplate(moneyValue, template, currency) {
  return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
    if (typeof placeholder !== 'string') return '';
    if (placeholder === 'currency') return currency;

    let thousandsSeparator = ',';
    let decimalSeparator = '.';
    let precision = 2;

    if (placeholder === 'amount') {
      // Default format.
    } else if (placeholder === 'amount_no_decimals') {
      precision = 0;
    } else if (placeholder === 'amount_with_comma_separator') {
      thousandsSeparator = '.';
      decimalSeparator = ',';
    } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
      thousandsSeparator = '.';
      precision = 0;
    } else if (placeholder === 'amount_no_decimals_with_space_separator') {
      thousandsSeparator = ' ';
      precision = 0;
    } else if (placeholder === 'amount_with_space_separator') {
      thousandsSeparator = ' ';
      decimalSeparator = ',';
    } else if (placeholder === 'amount_with_period_and_space_separator') {
      thousandsSeparator = ' ';
      decimalSeparator = '.';
    } else if (placeholder === 'amount_with_apostrophe_separator') {
      thousandsSeparator = "'";
      decimalSeparator = '.';
    }

    return formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
  });
}

/**
 * Formats money in cents.
 * @param {number} moneyValue - The money value in cents.
 * @param {string} thousandsSeparator - The thousands separator.
 * @param {string} decimalSeparator - The decimal separator.
 * @param {number} precision - The precision.
 * @returns {string} The formatted money value.
 */
function formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
  const roundedNumber = (moneyValue / 100).toFixed(precision);

  let [a, b] = roundedNumber.split('.');
  if (!a) a = '0';
  if (!b) b = '';

  a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

  return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
}
